// src/features/deadlines/components/EmailPreviewModal.tsx

import React, { useEffect, useState } from 'react';
import { X, Mail, Paperclip, User, Users, Send, Eye, FileText } from 'lucide-react';
import { EmailPreview } from '../../../services/email/types';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  preview: EmailPreview | null;
  isLoading?: boolean;
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  preview,
  isLoading
}) => {
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [iframeHeight, setIframeHeight] = useState(500);

  useEffect(() => {
    if (preview?.bodyHtml) {
      // Dynamische Höhenberechnung für den iFrame
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = preview.bodyHtml;
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.width = '600px';
      document.body.appendChild(tempDiv);
      
      const height = Math.min(Math.max(tempDiv.offsetHeight + 50, 400), 800);
      setIframeHeight(height);
      
      document.body.removeChild(tempDiv);
    }
  }, [preview?.bodyHtml]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Mail size={24} color="#667EEA" />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1A202C' }}>
              E-Mail Vorschau
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F7FAFC'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} color="#718096" />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#718096'
            }}>
              <div>Lade Vorschau...</div>
            </div>
          ) : preview ? (
            <>
              {/* E-Mail Metadaten */}
              <div style={{
                backgroundColor: '#F7FAFC',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={14} color="#718096" />
                  <strong>Von:</strong> {preview.from}
                </div>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={14} color="#718096" />
                  <strong>An:</strong> {preview.to}
                </div>
                {preview.cc && (
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={14} color="#718096" />
                    <strong>CC:</strong> {preview.cc}
                  </div>
                )}
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Mail size={14} color="#718096" />
                  <strong>Betreff:</strong> {preview.subject}
                </div>
                {preview.attachments && preview.attachments.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Paperclip size={14} color="#718096" style={{ marginTop: '2px' }} />
                    <div>
                      <strong>Anhänge:</strong>
                      <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                        {preview.attachments.map((att, idx) => (
                          <li key={idx} style={{ fontSize: '13px', color: '#4A5568' }}>
                            {att.filename}
                            {att.size && ` (${(att.size / 1024).toFixed(1)} KB)`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* View Mode Toggle */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
                borderBottom: '1px solid #E2E8F0',
                paddingBottom: '8px'
              }}>
                <button
                  onClick={() => setViewMode('html')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    backgroundColor: viewMode === 'html' ? '#667EEA' : 'transparent',
                    color: viewMode === 'html' ? '#FFFFFF' : '#718096',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Eye size={14} />
                  HTML-Ansicht
                </button>
                <button
                  onClick={() => setViewMode('text')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    backgroundColor: viewMode === 'text' ? '#667EEA' : 'transparent',
                    color: viewMode === 'text' ? '#FFFFFF' : '#718096',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <FileText size={14} />
                  Text-Ansicht
                </button>
              </div>

              {/* E-Mail Inhalt */}
              <div style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {viewMode === 'html' ? (
                  <iframe
                    srcDoc={preview.bodyHtml}
                    style={{
                      width: '100%',
                      height: `${iframeHeight}px`,
                      border: 'none'
                    }}
                    title="E-Mail HTML Vorschau"
                  />
                ) : (
                  <pre style={{
                    padding: '20px',
                    margin: 0,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '#2D3748'
                  }}>
                    {preview.bodyText}
                  </pre>
                )}
              </div>
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096'
            }}>
              Keine Vorschau verfügbar
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#667EEA',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5A67D8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667EEA'}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewModal;